// Editor controlado de una ESCALA DE TARIFA POR VOLUMEN: la lista de tramos "desde N
// paquetes, cada paquete cuesta X". Lo usan las dos escalas del operador (semanal y
// mensual) en CommercePricingSection.
//
// Cada tramo guarda SOLO su extremo inferior (`minQty`). El superior lo define el tramo
// siguiente, así que huecos y solapes son imposibles por construcción — el ejemplo con el
// que se especificó esta funcionalidad ya venía con uno ("de 501 a 1000" y "1000 +" chocan
// en 1000). El costo de ese modelo es que "desde 251" es menos legible que "de 251 a 500",
// y por eso el editor MUESTRA el rango derivado de cada fila: el operador piensa en rangos
// y el sistema guarda umbrales.
//
// Los avisos son NO BLOQUEANTES salvo el del tramo desde 0, que el backend rechaza. Se
// muestran mientras el operador escribe (una escala a medio cargar siempre está "mal") y
// el guardado los vuelve a validar del lado del servidor.
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/Button";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

let _uidSeq = 0;
const uid = () => `vt${++_uidSeq}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * API -> filas de UI. El id es solo para la `key` de React: `minQty` puede repetirse
 * mientras se escribe y el índice del array no es estable al quitar filas del medio.
 */
export const volumeTiersFromApi = (apiTiers) =>
  (Array.isArray(apiTiers) ? apiTiers : []).map((tier) => ({
    id: uid(),
    minQty: tier?.minQty != null ? String(tier.minQty) : "",
    unitPrice: tier?.unitPrice != null ? String(tier.unitPrice) : "",
  }));

/**
 * Filas de UI -> API. Descarta las filas en blanco y ORDENA por `minQty`.
 *
 * El orden importa: el rango de cada tramo se lee del siguiente, así que una escala
 * desordenada describe otros rangos. El backend vuelve a ordenar por su cuenta —no confía
 * en el cliente— pero ordenar acá hace que lo que se guarda sea lo que se ve.
 */
export const volumeTiersToApi = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .filter((r) => String(r?.minQty ?? "").trim() !== "" && String(r?.unitPrice ?? "").trim() !== "")
    .map((r) => ({
      minQty: Math.max(0, Math.round(num(r.minQty))),
      unitPrice: Math.max(0, Math.round(num(r.unitPrice))),
    }))
    .sort((a, b) => a.minQty - b.minQty);

/**
 * Problemas de una escala, en el orden en que conviene resolverlos. Se calcula sobre el
 * payload (ya ordenado y sin filas en blanco), no sobre las filas crudas: si no, escribir
 * el primer dígito de un umbral dispararía avisos que desaparecen solos.
 */
const auditTiers = (tiers, t) => {
  const problems = [];
  if (tiers.length === 0) return problems;

  if (tiers[0].minQty !== 0) {
    problems.push({
      blocking: true,
      text: t(
        "El primer tramo tiene que empezar en 0 paquetes. Si no, los comercios por debajo de esa cantidad quedan sin precio escalonado y se les cobra la tarifa plana.",
      ),
    });
  }

  const repeated = tiers
    .map((tier) => tier.minQty)
    .filter((minQty, i, all) => all.indexOf(minQty) !== i);
  if (repeated.length) {
    problems.push({
      blocking: true,
      text: t("Hay más de un tramo que empieza en {{qty}} paquetes.", {
        qty: repeated[0],
      }),
    });
  }

  // No es un error: un operador podría querer cobrar MÁS por volumen. Pero es lo bastante
  // raro como para avisar, porque lo normal es que sea un typo.
  const rises = tiers.some((tier, i) => i > 0 && tier.unitPrice > tiers[i - 1].unitPrice);
  if (rises) {
    problems.push({
      blocking: false,
      text: t("Algún tramo cuesta más que el anterior. Revisá que sea intencional."),
    });
  }

  return problems;
};

/**
 * Rango legible de un tramo, derivado del `minQty` del siguiente. Es lo que convierte
 * "desde 251" en "de 251 a 500", que es como el operador piensa la escala.
 */
const rangeLabel = (tiers, i, t) => {
  const from = tiers[i]?.minQty;
  if (from == null) return "";
  const next = tiers[i + 1]?.minQty;
  return next == null
    ? t("{{from}} o más", { from })
    : t("de {{from}} a {{to}}", { from, to: next - 1 });
};

export default function VolumeTiersEditor({
  title,
  description,
  rows,
  onChange,
  currency,
  disabled = false,
}) {
  const { t } = useTranslation();

  // Se audita y se etiqueta sobre el payload para que los rangos que se muestran sean los
  // que van a regir, aunque el operador haya cargado las filas en cualquier orden.
  const tiers = useMemo(() => volumeTiersToApi(rows), [rows]);
  const problems = useMemo(() => auditTiers(tiers, t), [tiers, t]);
  // Índice de fila -> posición en el payload ordenado, para poder rotular cada fila con su
  // rango real sin reordenar lo que el operador está escribiendo.
  const labelForRow = (row) => {
    const minQty = Math.round(num(row.minQty));
    const i = tiers.findIndex((tier) => tier.minQty === minQty);
    return i < 0 ? "" : rangeLabel(tiers, i, t);
  };

  const setRow = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...rows, { id: uid(), minQty: "", unitPrice: "" }]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-fg">{title}</label>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          iconLeft={<Plus className="w-4 h-4" />}
          onClick={addRow}
        >
          {t("Agregar tramo")}
        </Button>
      </div>
      {description && <p className="text-xs text-fg-muted mb-2">{description}</p>}

      {rows.length === 0 && (
        <p className="text-xs text-fg-muted">
          {t("Sin tramos: esta escala no se puede usar hasta que cargues al menos uno.")}
        </p>
      )}

      {rows.map((row, i) => (
        <div key={row.id ?? i} className="flex gap-2 mb-2 items-end flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">{t("Desde (paquetes)")}</span>
            <input
              type="number"
              min="0"
              step="1"
              disabled={disabled}
              className={`${inputCls} max-w-[140px]`}
              value={row.minQty}
              onChange={(e) => setRow(i, { minQty: e.target.value })}
              placeholder={t("Ej: 21")}
              aria-label={t("Desde (paquetes)")}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-fg-muted">
              {t("Precio por paquete")} ({currency})
            </span>
            <input
              type="number"
              min="0"
              disabled={disabled}
              className={`${inputCls} max-w-[160px]`}
              value={row.unitPrice}
              onChange={(e) => setRow(i, { unitPrice: e.target.value })}
              placeholder={t("Ej: 3000")}
              aria-label={t("Precio por paquete")}
            />
          </div>
          {/* El rango derivado: el operador cargó un umbral y acá ve el tramo que produce. */}
          <span className="text-xs text-fg-muted h-9 flex items-center min-w-[110px]">
            {labelForRow(row)}
          </span>
          <button
            type="button"
            disabled={disabled}
            className="h-9 px-2 text-fg-muted hover:text-status-error disabled:opacity-40"
            onClick={() => removeRow(i)}
            aria-label={t("Quitar tramo")}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {problems.map((problem) => (
        <div
          key={problem.text}
          className={`flex items-start gap-2 text-xs mt-1 ${
            problem.blocking ? "text-status-error" : "text-fg-muted"
          }`}
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>{problem.text}</span>
        </div>
      ))}
    </div>
  );
}
